import { TreeNodeModel } from '@creditsights/styleguide-web';
import { isEmpty, toNumber } from 'lodash';
import { CONTENT_TYPE_FILTER_URL_KEY } from '../components/FiltersBar/FiltersBar.logic';
import { EVENT_DRIVEN_REPORT, EVENT_DRIVEN_TITLES, NEWS, NEWS_CATEGORIES } from '../components/FiltersBar/constants';
import {
  CoreScoreFilterType,
  EventDrivenTag,
  FilterType,
  Filters,
  RiskFilterType,
  TagOptionParameters,
  TreeModelParameters,
} from '../models';
import { CoreScoresFilterParams, RangesParams, RpFilterParams } from '../services/issuers/issuersQueryParams';
import { TagsFilterParams } from '../services/tags/tags.model';

export const convertToTreeModel = ({ options, filter }: TreeModelParameters): TreeNodeModel[] => {
  const filteredOptions = filter ? options.filter(filter) : options;
  return filteredOptions.map((option) =>
    convertTagOption({
      option,
      filter,
    })
  );
};

export const convertRiskOptionsToTreeModel = (values: string[] = []): TreeNodeModel[] =>
  values.map((value) => ({
    key: value,
    title: value,
  }));

const MAX_CONVERT_TAG_DEPTH = 2;

const convertTagOption = ({ option, filter, currentDepth = 1 }: TagOptionParameters): TreeNodeModel => {
  let children = filter ? option.children?.filter(filter) : option.children;

  if (option.tagId === EVENT_DRIVEN_REPORT) {
    children = Object.values(EventDrivenTag).map((value) => ({
      tagId: value,
      displayName: EVENT_DRIVEN_TITLES[value],
    }));
  }

  if (option.tagId === NEWS) {
    const alreadyExists: string[] = [];
    const nextChildren = NEWS_CATEGORIES.map(({ displayName, pattern }, index) => {
      const foundItems =
        children?.filter((item) => !alreadyExists.includes(item.tagId) && pattern.test(item.tagId)) || [];

      alreadyExists.push(...foundItems.map((item) => item.tagId));

      return {
        tagId: `${NEWS}-${index}`,
        displayName,
        children: foundItems,
      };
    });

    children = [
      ...nextChildren.filter((item) => item.children.length > 0),
      ...(children?.filter((item) => !alreadyExists.includes(item.tagId)) || []),
    ];
  }

  return {
    key: option.tagId,
    title: option.displayName,
    children:
      currentDepth < MAX_CONVERT_TAG_DEPTH || option.tagId === EVENT_DRIVEN_REPORT || option.tagId?.startsWith(NEWS)
        ? children?.map((child) =>
            convertTagOption({
              option: child,
              filter,
              currentDepth: currentDepth + 1,
            })
          )
        : undefined,
  };
};

export const filterNewsTags = (id: string): boolean => !new RegExp(`^${NEWS}-[0-9]+$`).test(id);

const isOptionOfRiskFilterType = <T extends string>(option: T): boolean => option in RiskFilterType;

const isOptionOfCoreScoreFilterType = <T extends string>(option: T): boolean => option in CoreScoreFilterType;

const isOptionOfFilterType = <T extends string>(option: T): boolean => option in FilterType;

export const isOptionOfAnyFilterType = <T extends string>(option: T): boolean =>
  isOptionOfFilterType(option) || isOptionOfRiskFilterType(option) || isOptionOfCoreScoreFilterType(option);

/**
 * Returns filters in a format ready to call API
 * Filters are grouped to support AND/OR logic.
 * For example passing: `{ tags: "source": ["type-tag/news"], "region": ["region/europe", "region/america"]}`
 * would return `[["type-tag/news"], ["region/europe", "region/america"]]`
 * which would logically translate to: "news" AND ("europe" or "america")
 */
export const getGroupedFilterTags = (filters: Filters | null): TagsFilterParams => {
  if (!filters?.tags) {
    return [];
  }

  return Object.keys(filters.tags)
    .filter((key) => !isOptionOfRiskFilterType(key) && !isOptionOfCoreScoreFilterType(key))
    .map((key) => filters.tags![key]);
};

export const getRPFilterParams = (filters: Filters | null): RpFilterParams[] => {
  if (!filters?.tags) {
    return [];
  }

  const tagFilters: RpFilterParams[] = Object.keys(filters.tags)
    .filter(isOptionOfRiskFilterType)
    .reduce<RpFilterParams[]>((acc, key) => {
      const values = filters.tags![key];

      if (isEmpty(values)) {
        return acc;
      }

      const filterParams: RpFilterParams = {
        key,
        values,
      };

      return [...acc, filterParams];
    }, []);

  const rangeFilters: RpFilterParams[] = Object.entries(filters.ranges || {})
    .map(([key, range]) => ({
      key,
      valueFrom: range.from,
      valueTo: range.to,
      excludeValue: range.excluded,
    }))
    .filter((range) => range.valueFrom != null || range.valueTo != null);

  return [...tagFilters, ...rangeFilters];
};

export const getCoreScoreFilterParams = (filters: Filters | null): CoreScoresFilterParams[] => {
  if (!filters?.tags) {
    return [];
  }

  let assessment;
  let outlook;
  const tagFilters: CoreScoresFilterParams[] = Object.keys(filters.tags)
    .filter(isOptionOfCoreScoreFilterType)
    .reduce<CoreScoresFilterParams[]>((acc, key) => {
      const values = filters.tags![key].map((item) => toNumber(item));

      if (isEmpty(values)) {
        return acc;
      }

      const filterParams: CoreScoresFilterParams = {
        key,
        values,
      };

      if (key === 'assessment') {
        assessment = filterParams;
        return [...acc];
      }
      if (key === 'outlook') {
        outlook = filterParams;
        return [...acc];
      }
      return [...acc, filterParams];
    }, []);

  if (assessment) {
    const assessmentFilters = constructRangeValues(assessment);
    tagFilters.push(assessmentFilters);
  }
  if (outlook) {
    const outlookFilters = constructRangeValues(outlook);
    tagFilters.push(outlookFilters);
  }
  return [...tagFilters];
};

const findUpperRange = (upper: number): number => {
  if (upper === 4) {
    return 1;
  }
  return 0.99;
};
const constructRangeValues = (cumulative: CoreScoresFilterParams): CoreScoresFilterParams => {
  const cumulativeFilters: RangesParams[] = [];
  const cumulativeValues = cumulative.values ?? [];
  cumulativeValues.sort();
  const { key } = cumulative;
  let valueFrom = cumulativeValues[0];
  for (let i = 0; i < cumulativeValues.length - 1; i += 1) {
    if (cumulativeValues[i + 1] !== cumulativeValues[i] + 1) {
      cumulativeFilters.push({ valueFrom, valueTo: cumulativeValues[i] + findUpperRange(cumulativeValues[i]) });
      valueFrom = cumulativeValues[i + 1];
    }
  }
  cumulativeFilters.push({
    valueFrom,
    valueTo:
      cumulativeValues[cumulativeValues.length - 1] + findUpperRange(cumulativeValues[cumulativeValues.length - 1]),
  });

  return { key, ranges: cumulativeFilters };
};

export const getTitleContainsFilter = (titles: string[] | undefined): string | undefined => {
  return titles ? titles.map((title) => `content.title:"${title}"`).join(' || ') : undefined;
};

export const validateRangeValues = (
  from: number | undefined,
  to: number | undefined,
  min: number,
  max: number,
  isFractional?: boolean
): boolean => {
  if ((from != null && Number.isNaN(from)) || (to != null && Number.isNaN(to))) {
    return false;
  }

  const validFrom = from == null || (from >= min && from <= max);
  const validTo = to == null || (to >= min && to <= max);

  if (!validFrom || !validTo) {
    return false;
  }

  const valueFrom = from ?? min;
  const valueTo = to ?? max;
  const rangeValid = valueTo >= valueFrom;

  if (!rangeValid) {
    return false;
  }

  if (isFractional) {
    return true;
  }

  return Number.isInteger(valueTo) && Number.isInteger(valueFrom);
};

/* eslint-disable no-param-reassign */
export const deleteSourceAndContentType = (filters: Filters): void => {
  delete filters.tags?.[CONTENT_TYPE_FILTER_URL_KEY];
  delete filters.tags?.[FilterType.source];
  delete filters.tags?.[FilterType.covenants_source];
  delete filters.tags?.[FilterType.news_source];
};
/* eslint-enable no-param-reassign */
